import { Component, ViewChild, ElementRef, Inject, OnDestroy, AfterViewInit, HostListener, Input, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { io, Socket } from 'socket.io-client';
import { NgTerminal } from 'ng-terminal';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { ImageNameDialogComponent } from './image-name-dialog/image-name-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FileUploadDialogComponent } from './file-upload-dialog/file-upload-dialog.component';
import { FileDownloadDialogComponent } from './file-download-dialog/file-download-dialog.component'


@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.css'
})
export class ShellComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('shellOutput') shellOutput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('ngTerm', { static: false }) terminal!: NgTerminal;
  @ViewChild('ngTerm', { read: ElementRef, static: false }) terminalElementRef!: ElementRef;
  showContextMenu = false;
  contextMenuX = 0;
  contextMenuY = 0;

  @Input() id: string = '';
  shellContent: string = '';
  terminalContent: string = '';
  lastUpdateAgo: string = '';
  unixTimestamp: number | null = null;
  intervalId: any;
  title: string = '';
  isLoading: boolean = true; // Flag to show loading spinner
  linesToPull: number = 100; // Number of lines to pull from the shell output
  activeTabIndex: number = 0;
  activeHost: string | null = null;
  autoStartLog: boolean = true;

  cols: number = 80;
  rows: number = 24;

  // SSH Terminal
  private socket!: Socket;
  redirectData: string;

  @Input() systemIP: string = '';
  @Input() systemNumber: string = '';
  @Input() version: string = '';
  @Input() releaseDate: string = '';
  @Input() jsdVersion: string = '';
  @Input() terminalAdmin: boolean = false;

  systemNumberIp: string = '';

  uploadProgress = 0;

  // --- File download state ---
  downloadFileSize = 0;
  downloadFileName = '';
  downloadedChunks: string[] = [];
  downloadProgress = 0;
  downloadExpectedSize = 0;
  isDownloading = false;

  haveSetupTerminalListener: boolean = false;

  constructor(
    public auth: AuthService,
    private afs: AngularFirestore,
    private notifierService: NotificationService,
    public dialogRef: MatDialogRef<ShellComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.id = data.id || '';




    // this.socket = io('https://main.friggkantine.no', {
    //   path: '/service/terminal/socket.io',
    //   secure: true, // Use secure connection
    // });


  }

  ngOnInit(): void {
    if (!this.terminalAdmin) {
      this.autoStartLog = true;
    } else if (localStorage.getItem('autoStartLog') !== null && this.terminalAdmin) {
      this.autoStartLog = localStorage.getItem('autoStartLog') === 'true';
    } else {
      this.autoStartLog = true;
    }

    if (this.systemNumber) {
      const first = this.systemNumber.toString().substring(0, 2);
      const second = this.systemNumber.toString().substring(2, 4);
      this.systemNumberIp = `10.9.${first}.${second}`;
    } else {
      console.warn('systemNumber is not provided.');
    }

    (async () => {
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for 100ms to ensure everything is set up
      this.connectSocket();
      this.onResize();
    })();
    this.uploadProgress = 0;
  }


  private async connectSocket(useNumberToConnect: boolean = false) {

    if (this.terminal) {

      this.terminal.setRows(32);
      this.terminal.setCols(160);

      this.terminal.write('Connecting to SSH terminal...\r\n');
    } else {
      console.error('Terminal not initialized');
    }

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.socket = io('https://main.friggkantine.no', {
      path: '/service/terminal/socket.io',
      secure: true, // Use secure connection
      transports: ["polling"], // Force TCP transport
    });

    this.socket.on('file-upload-progress', (data: any) => {
      this.uploadProgress = Math.round((data.receivedBytes / data.totalBytes) * 100);
      if (this.terminal) {
        // Use carriage return to overwrite the same line
        this.terminal.write(`\rUpload local -> server - ${this.uploadProgress}%`);
      }
    });
    this.socket.on('upload-complete', (data: any) => {
      this.notifierService.showNotification('success', 'Upload complete!');
      this.socket.emit('data', '\x03');

      if (this.terminal) {
        this.terminal.write('\x03'); // Send Ctrl+C to stop any running process
      }
      this.uploadProgress = 0;
    });
    this.socket.on('file-upload-ack', () => {
      // Ready to send chunks
    });

    this.socket.on('client-init', async (data: any) => {
      // Clear terminal before writing
      this.terminal.write('\x1b[2J\x1b[H'); // Clear terminal
      this.terminal.write(`\r\nServer requested client initialization.`);
      // Get refresh token or user ID from AuthService
      const token = await this.auth.getToken();
      if (!token) {
        console.error('No token available');
        return;
      }

      this.socket.emit('register', { id: this.id, token, useNumberToConnect });
    });

    this.socket.on('ready', (data: any) => {
      this.onResize();
      if (this.autoStartLog) {
        this.startLiveLog();
      }
    });

    this.socket.on('message', (message: string) => {
      this.terminal.write(`\r\n${message}`);
    });

    this.socket.on('message-same-line', (message: string) => {
      if (this.terminal) {
        this.terminal.write(`\r${message}`);
      }
    });

    this.socket.on('no-connect', () => {
      // Reconnect after a delay
      setTimeout(() => {
        this.connectSocket();
      }, 5000);
    });

    this.socket.on('data', (data: string) => {
      if (this.terminal) {

        this.terminal.write(data);
        this.terminalContent += data; // Append data to terminalContent

        // Scroll to the bottom of the terminal after writing data
        setTimeout(() => {
          if (this.terminalElementRef) {
            const viewport = this.terminalElementRef.nativeElement.querySelector('.xterm-viewport');
            if (viewport) {
              viewport.scrollTop = viewport.scrollHeight;
            }
          }
        });
      }
    });
    this.socket.on('close', () => {
      if (this.terminal) {
        this.terminal.write('\r\nSSH session closed.');

        // Try to reconnect after a delay
        setTimeout(() => {
          this.connectSocket();
        }, 5000);



      }
    });
    this.socket.on('error', (err: string) => {
      if (this.terminal) {
        this.terminal.write(`\r\nSSH error: ${err}`);
      }
    });



    if (this.terminal && !this.haveSetupTerminalListener) {
      this.haveSetupTerminalListener = true;
      this.terminal.onData().subscribe((data: string) => {
        if (this.socket && this.socket.connected) {
          this.socket.emit('data', data);
        }
      });
    }

    // --- File download handlers ---
    this.socket.on('file-download-chunk', (data: any) => {
      // data: { chunk, chunkSize, totalSize }
      this.downloadedChunks.push(data.chunk);
      this.downloadFileSize += data.chunkSize;
      this.downloadExpectedSize = data.totalSize || this.downloadExpectedSize;
      this.downloadProgress = Math.round((this.downloadFileSize / (this.downloadExpectedSize || 1)) * 100);
      this.isDownloading = true;
      if (this.terminal) {
        this.terminal.write(`\rDownload server -> local - ${this.downloadProgress}%`);
      }
    });
    this.socket.on('file-download-complete', (data: any) => {
      if (this.terminal) {
        this.terminal.write(`\r\nDownload complete: ${data.remotePath}                  `);
      }
      // --- Binary-safe chunk assembly ---
      const byteArrays: BlobPart[] = [];
      for (const base64Chunk of this.downloadedChunks) {
        const binaryString = atob(base64Chunk);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        byteArrays.push(bytes);
      }
      const blob = new Blob(byteArrays);
      const fileName = data.remotePath.split('/').pop() || 'downloaded_file';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      // Reset download state
      this.downloadedChunks = [];
      this.downloadFileSize = 0;
      this.downloadProgress = 0;
      this.downloadExpectedSize = 0;
      this.isDownloading = false;
    });
  }

  async startLiveLog() {
    if (this.socket) {
      this.socket.emit('function', { function: 'docker-log' })
    } else {
      console.error('Socket not connected');
    }
  }

  async toggleAutoStartLog() {
    this.autoStartLog = !this.autoStartLog;

    // Save to localStorage
    localStorage.setItem('autoStartLog', this.autoStartLog ? 'true' : 'false');
  }

  async startLog() {
    if (this.socket) {
      this.socket.emit('function', { function: 'docker-log' })
    } else {
      console.error('Socket not connected');
    }
  }

  async restartContainer() {

    if (!this.verifyActionYesNo('RESTART', 'container restart')) {
      return;
    }

    if (this.socket) {
      this.socket.emit('function', { function: 'restart-container' });
    } else {
      console.error('Socket not connected');
    }
  }



  restartComputer() {

    if (!this.verifyActionDangerous('RESTART', 'computer restart (DANGEROUS)')) {
      return;
    }

    if (this.socket) {
      this.socket.emit('function', { function: 'restart-computer' });

      // Try to reconnect after a delay
      setTimeout(() => {
        this.connectSocket();
      }, 5000);

    } else {
      console.error('Socket not connected');
    }
  }

  //      <button mat-menu-item (click)="pullContainer()">Pull container</button>
  // <button mat-menu-item (click)="dockerComposeUp()">Docker compose up</button>

  async pullContainer() {

    // Open dialog to get container name
    const dialogRef = this.dialog.open(ImageNameDialogComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (this.socket) {
          this.socket.emit('function', { function: 'pull-new-container', imageName: result });
        } else {
          console.error('Socket not connected');
        }
      }
    });
  }

  updateSoftware() {

    if (!this.verifyActionYesNo('UPDATE', 'Update software (DANGEROUS)')) {
      return;
    }

    if (this.socket) {
      this.socket.emit('function', { function: 'update-software' });
    } else {
      console.error('Socket not connected');
    }
  }

  async connectUsingNumber() {
    if (this.socket) {
      await this.socket.disconnect();
      this.connectSocket(true);
    } else {
      console.error('Socket not connected');
    }
  }

  async connectUsingIP() {
    if (this.socket) {
      await this.socket.disconnect();
      this.connectSocket();
    } else {
      console.error('Socket not connected');
    }
  }

  dockerComposeUp() {
    if (!this.verifyActionYesNo('COMPOSE UP', 'docker compose up')) {
      return;
    }
    if (this.socket) {
      this.socket.emit('function', { function: 'docker-compose-up' });
    } else {
      console.error('Socket not connected');
    }
  }

  dockerComposeDown() {
    if (!this.verifyActionDangerous('COMPOSE DOWN', 'Shuting down program.')) {
      return;
    }
    if (this.socket) {
      this.socket.emit('function', { function: 'docker-compose-down' });
    } else {
      console.error('Socket not connected');
    }
  }

  openComposeFile() {
    if (this.socket) {
      this.socket.emit('function', { function: 'open-compose-file' });
    } else {
      console.error('Socket not connected');
    }
  }

  verifyActionDangerous(command: string, actionName: string): boolean {
    const confirmation = window.prompt(`Type "${command}" to confirm ${actionName}.`);

    // Did not match
    if (!confirmation || confirmation.trim().toUpperCase() !== command) {
      this.notifierService.showNotification('info', `${actionName} cancelled.`);
      return false;
    }

    return true;
  }

  verifyActionYesNo(command: string, actionName: string): boolean {
    const confirmation = window.confirm(`Are you sure you want to proceed with ${actionName}?`);
    return confirmation;
  }

  runInTerminal(command: string) {
    if (this.socket) {
      this.socket.emit('data', `${command}\r`);
    }
  }

  exportTerminalToTextFile() {
    if (!this.terminalContent) return;
    const plainContent = this.terminalContent.replace(
      // Remove most ANSI escape codes
      /[\u001b\u009b][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]/g,
      ''
    );
    const blob = new Blob([plainContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.title.replace(/\s+/g, '_')}_terminal_output.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  onCommand(action: string) {
    if (action === 'restart_container' || action === 'restart_computer') {
      const confirmMsg = action === 'restart_container'
        ? 'Are you sure you want to restart the program?'
        : 'Are you sure you want to restart the computer?';
      if (!window.confirm(confirmMsg)) {
        return;
      }
    }
    if (!this.id) return;
    let updateObj: any = {};
    if (action === 'restart_container' || action === 'restart_computer' || action === 'docker_status' || action === 'pm2_status' || action === 'system_status' || action === 'network_status') {
      updateObj = {
        function: action,
        update: true
      };
    }
    this.afs.collection('system_status').doc(this.id).collection('shell').doc('shell').update(updateObj)
      .then(() => {
        this.isLoading = true;
      })
      .catch((error) => {
        console.error(`Error sending ${action} request:`, error);
      });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  ngAfterViewInit(): void {
    // ...existing terminal setup...
    window.addEventListener('resize', this.onResize.bind(this));
    this.onResize(); // Initial sizing
    // ...existing code...
  }

  onResize() {
    if (this.terminalElementRef && this.terminal) {
      const container = this.terminalElementRef.nativeElement;
      // Estimate rows/cols based on container size and font size
      const charWidth = 9; // Adjust for your font
      const charHeight = 17; // Adjust for your font
      this.cols = Math.floor(container.offsetWidth / charWidth);
      this.rows = Math.floor((container.offsetHeight - 20) / charHeight);
      this.terminal.setCols(this.cols);
      this.terminal.setRows(this.rows);
      if (this.socket) this.socket.emit('resize', { cols: this.cols, rows: this.rows });
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize.bind(this));
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  onContextMenu(event: MouseEvent): void {
    event.preventDefault(); // Prevent default browser context menu

    if (!this.terminal) {
      console.error('Terminal not initialized');
      return;
    }

    if (!this.terminal.underlying.hasSelection() && navigator.clipboard) {
      // Need to paste
      this.pasteText();

      return;
    }

    if (this.terminal.underlying.hasSelection()) {
      this.copyText();
      return;
    }

  }


  // --- Copy Function ---
  copyText(): void {

    // Put selection into clipboard
    navigator.clipboard.writeText(this.terminal.underlying.getSelection() || '').then(() => {

    }).catch(err => {
      console.error('Could not copy text: ', err);
    });

    this.terminal.underlying.clearSelection();

    this.showContextMenu = false;
  }

  // --- Paste Function ---
  // Pasting requires the user to interact with the browser's Clipboard API.
  async pasteText(): Promise<void> {
    try {
      // Use the browser's Clipboard API to read text
      const text = await navigator.clipboard.readText();

      // Write the text to the terminal input
      this.terminal.underlying.paste(text);
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
      // Fallback for browsers that block clipboard API (e.g., non-HTTPS)
      alert('Paste failed. Please use Ctrl+Shift+V or Cmd+V.');
    } finally {
      this.showContextMenu = false;
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showContextMenu) {
      this.showContextMenu = false;
    }
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.notifierService.showNotification('success', 'Copied to clipboard!');
    }).catch(err => {
      this.notifierService.showNotification('error', 'Failed to copy to clipboard.');
      console.error('Could not copy text: ', text, err);
    });
  }

  arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  openFileUploadDialog() {
    const dialogRef = this.dialog.open(FileUploadDialogComponent, {
      width: '400px',
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result && result.file && result.targetPath) {
        const file: File = result.file;
        if (!file) {
          this.notifierService.showNotification('error', 'No file selected.');
          return;
        }
        if (!this.socket) {
          this.notifierService.showNotification('error', 'Socket not connected.');
          return;
        }
        try {
          if (this.terminal) {
            this.terminal.write(`\r\n`);
          }
          this.socket.emit('file-upload-start', { fileName: file.name, fileSize: file.size });
          const chunkSize = 64 * 1024; // 64KB
          let offset = 0;
          while (offset < file.size) {
            const chunk = await file.slice(offset, offset + chunkSize).arrayBuffer();
            const base64Chunk = this.arrayBufferToBase64(chunk);
            this.socket.emit('file-upload-chunk', { chunk: base64Chunk, chunkSize: chunk.byteLength });
            offset += chunkSize;
          }
          this.socket.emit('file-upload-end');
          this.notifierService.showNotification('info', 'File upload started.');
        } catch (err) {
          this.notifierService.showNotification('error', 'File upload failed.');
          console.error('File upload error:', err);
          this.uploadProgress = 0;
        }
      } else {
        this.notifierService.showNotification('info', 'File upload cancelled.');
      }
    });
  }

  openFileDownloadDialog() {
    const dialogRef = this.dialog.open(FileDownloadDialogComponent, {
      width: '400px',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.remotePath) {
        this.requestFileDownload(result.remotePath);
      } else {
        this.notifierService.showNotification('info', 'File download cancelled.');
      }
    });
  }

  // --- Trigger file download ---
  startFileDownload(remotePath: string, fileName?: string) {
    if (!remotePath) return;
    this.isDownloading = true;
    this.downloadedChunks = [];
    this.downloadProgress = 0;
    this.downloadFileName = fileName || remotePath.split('/').pop() || 'downloaded_file';
    // Optionally, set file size if known
    this.downloadFileSize = 0; // If server sends size, set it here
    if (this.terminal) {
      this.terminal.write(`\r\nDownloading ${remotePath} ...\n\n`);
    }
    this.socket.emit('file-download-request', { remotePath });

  }

  // --- Save downloaded file ---
  saveDownloadedFile() {
    // Combine base64 chunks
    const base64Data = this.downloadedChunks.join('');
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.downloadFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  requestFileDownload(remotePath: string) {
    if (this.socket && this.socket.connected) {
      this.downloadedChunks = [];
      this.downloadFileSize = 0;
      this.downloadProgress = 0;
      this.downloadFileName = remotePath.split('/').pop() || 'downloaded_file';
      this.terminal.write(`\r\nRequesting download: ${remotePath}\n`);
      this.socket.emit('file-download-request', { remotePath });
    } else {
      this.notifierService.showNotification('error', 'Socket not connected.');
    }
  }

  watchLatestFile(remotePath: string) {
    if (this.socket && this.socket.connected) {
      this.terminal.write(`\r\nWatching file: ${remotePath}`);
      this.socket.emit('function', { function: "watch-latest-file", remotePath });
    } else {
      this.notifierService.showNotification('error', 'Socket not connected.');
    }
  }
}
